import { Printer } from "lucide-react";
import { printTools } from "@/lib/scroll";
import { DownloadChoices } from "@/components/DownloadChoices";
import { TopBar } from "@/components/TopBar";
import { Footer } from "@/components/Footer";
import { FullFooter } from "@/components/workbook/PackPage";

const Box = () => <span className="inline-block h-4 w-4 shrink-0 border-2 border-current align-middle" aria-hidden="true" />;
const Line = ({ label, w = "flex-1" }) => <span className="flex items-end gap-2"><span className="font-bold">{label}</span><span className={`${w} min-w-[60px] border-b-2 border-dotted border-current`} aria-hidden="true">&nbsp;</span></span>;
const Ticks = ({ items }) => <ul className="space-y-2">{items.map((t) => <li key={t} className="flex items-center gap-3"><Box />{t}</li>)}</ul>;
const Head = ({ children }) => <h3 className="mb-3 mt-5 border-b-2 border-current pb-1 text-sm font-extrabold uppercase tracking-[0.06em] first:mt-0">{children}</h3>;
const IdRow = ({ who = "Staff initials" }) => <div className="mb-4 grid gap-3 sm:grid-cols-3"><Line label="Date" /><Line label="Pupil reference code" /><Line label={who} /></div>;

const CARDS = [
  ["GO", "I am processing", "I understand the current task and am working through it independently.", "#1d7a3a", "#e6f4ea"],
  ["PAUSE", "I need adaptive teaching", "I want to do the work, but I need the instructions broken down or modelled.", "#8a5a00", "#fdf1d8"],
  ["STOP", "I am experiencing overwhelm", "I need to use my designated sensory break space immediately. No questions asked.", "#a3201b", "#fbe4e2"],
];

const TrafficCards = () => (
  <div className="grid gap-4 sm:grid-cols-3">
    {CARDS.map(([big, head, text, fg, bg]) => (
      <div key={big} className="avoid-break rounded-2xl border-[3px] border-dashed p-5 text-center" style={{ borderColor: fg, background: bg, color: fg }}>
        <p className="text-4xl font-extrabold tracking-[0.1em]">{big}</p>
        <p className="mt-3 text-lg font-extrabold uppercase">{head}</p>
        <p className="mt-2 font-semibold">{text}</p>
      </div>
    ))}
  </div>
);

const Ebsa = () => (
  <div>
    <IdRow />
    <Head>1. Energy and emotional battery level (circle one)</Head>
    <div className="flex flex-wrap justify-between gap-2">{["0% Empty", "25%", "50% Steady", "75%", "100% Full"].map((b) => <span key={b} className="rounded-full border-2 border-current px-3 py-1 font-bold">{b}</span>)}</div>
    <Head>2. What might be making today hard? (tick all that apply)</Head>
    <Ticks items={["Noise or sensory overload", "Unstructured social time (break or lunch)", "Worry about a specific lesson", "Friendship or peer difficulties", "Something else:"]} />
    <Head>3. Agreed plan for this morning</Head>
    <Ticks items={["10 minutes of quiet reading in the library base", "Workstation set up with noise-cancelling headphones", "Check-in with a named trusted adult", "Other:"]} />
    <div className="mt-5 grid gap-3 sm:grid-cols-2"><span className="flex items-center gap-3"><Box /> Pupil agreed the plan</span><Line label="Staff signature" /></div>
  </div>
);

const Anchor = () => (
  <div className="space-y-4">
    <div className="rounded-xl border-2 border-current p-4"><p className="font-extrabold">MY CURRENT TARGET</p><Line label="I am working on:" /></div>
    <div className="rounded-xl border-2 border-current p-4"><p className="font-extrabold">MY PARKING LOT</p><p className="text-sm">Ideas I want to say out loud later:</p><div className="mt-2 space-y-3"><Line label="•" /><Line label="•" /></div></div>
    <div className="rounded-xl border-2 border-current p-4"><p className="font-extrabold">MY REWARD CHECK</p><p className="mt-2 flex items-center gap-3"><Box /> Is this task done? YES! Time to check in.</p></div>
  </div>
);

const Steps = () => (
  <ol className="grid gap-3 sm:grid-cols-3">
    {["Highlight the keywords", "Say the answer out loud", "Write one sentence"].map((s, i) => (
      <li key={s} className="flex items-start gap-3 rounded-xl border-2 border-current p-4"><Box /><span><span className="block text-2xl font-extrabold">Step {i + 1}</span><span className="font-bold">{s}</span></span></li>
    ))}
  </ol>
);

const Grid = ({ cols, rows = 5 }) => (
  <div className="overflow-x-auto" data-lenis-prevent>
  {rows > 0 && <p className="no-print mb-2 text-xs text-ink-soft sm:hidden">Swipe the table sideways to see every column.</p>}
  <table className="w-full min-w-[520px] border-collapse text-sm">
    <thead><tr>{cols.map((c) => <th key={c} scope="col" className="border-2 border-current px-2 py-1.5 text-left">{c}</th>)}</tr></thead>
    <tbody>{Array.from({ length: rows }, (_, i) => <tr key={i}>{cols.map((c) => <td key={c} className="h-9 border-2 border-current px-2">&nbsp;</td>)}</tr>)}</tbody>
  </table>
  </div>
);

const Auditory = () => (
  <div><IdRow who="Logged by (initials)" /><p className="mb-3 text-sm">Contexts: lunch hall, transitions, PE, assembly, classroom, other. Intensity: 1 (mild) to 5 (severe).</p>
    <Grid cols={["Date / time", "Context", "Intensity 1–5", "Ear defenders worn?", "What helped"]} rows={7} /></div>
);

const Contrast = () => (
  <div><IdRow who="Checked by (initials)" />
    <Head>Font size (tick the most comfortable)</Head>
    <div className="flex flex-wrap items-end gap-5">{[12, 14, 16, 18, 24].map((s) => <span key={s} className="flex items-center gap-2"><Box /><span style={{ fontSize: `${s}pt` }}>Aa {s}pt</span></span>)}</div>
    <Head>Line spacing</Head>
    <div className="grid gap-3 sm:grid-cols-3">{[1, 1.5, 2].map((l) => <div key={l} className="flex gap-2"><Box /><p style={{ lineHeight: l }} className="text-sm">Reading line spacing {l}. The quick brown fox jumps over the lazy dog.</p></div>)}</div>
    <Head>Background tint</Head>
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">{[["White", "#ffffff"], ["Cream", "#fbf3dc"], ["Pale blue", "#dfeefa"], ["Pale yellow", "#fdf8c4"]].map(([n, bg]) => <span key={n} className="flex items-center gap-2 rounded-lg border-2 border-current p-3" style={{ background: bg, color: "#111" }}><Box />{n}</span>)}</div>
  </div>
);

const Seizure = () => (
  <div>
    <p className="mb-4 rounded-xl border-2 border-current p-3 text-sm font-bold">Follow the pupil's Individual Healthcare Plan and your school's medical emergency procedures first. This sheet is for recording only and does not replace them.</p>
    <IdRow who="Logged by (initials)" />
    <Head>1. Timings</Head>
    <div className="grid gap-3 sm:grid-cols-3"><Line label="Start time" /><Line label="End time" /><Line label="Total duration (mins)" /></div>
    <Head>2. Recovery observations (tick at each interval)</Head>
    <Grid cols={["Interval", "Drowsy", "Confused", "Speech delay", "Unresponsive", "Alert", "Oriented", "Walking", "Head pain"]} rows={4} />
    <Head>3. Sign-off</Head>
    <Ticks items={["Pupil back to their usual baseline. Ready to resume supervised learning.", "Medical help called. Parent or carer collection logged."]} />
    <div className="mt-4 grid gap-3 sm:grid-cols-2"><Line label="Staff signature" /><Line label="Time form closed" /></div>
  </div>
);

const Desk = () => (
  <div><IdRow who="Checked by (initials)" />
    <Grid cols={["Check", "In place?", "Notes / action"]} rows={0} />
    <div className="overflow-x-auto"><table className="-mt-px w-full min-w-[520px] border-collapse text-sm"><tbody>
      {["Seating and posture: feet supported, chair and desk at the right height", "Angle board in place where needed", "Adapted grip or pen within easy reach", "Page stabilised (non-slip mat or clipboard)", "Workspace layout: materials within reach, clear space to work"].map((r) => (
        <tr key={r}><td className="w-1/2 border-2 border-current px-2 py-2 font-semibold">{r}</td><td className="border-2 border-current px-2"><span className="flex gap-4"><span className="flex items-center gap-1"><Box />Yes</span><span className="flex items-center gap-1"><Box />No</span></span></td><td className="border-2 border-current px-2">&nbsp;</td></tr>
      ))}
    </tbody></table></div>
  </div>
);

export const PRINTABLES = [
  ["pr-traffic", "Visual communication exit cards", "Traffic-light cards for the desk to support non-verbal communication and emotional regulation. Cut along the dashed lines.", TrafficCards],
  ["pr-ebsa", "EBSA morning check-in", "A quick arrival sheet for pastoral leads, ELSAs and SENDCOs to use at the start of the day.", Ebsa],
  ["pr-anchor", "ADHD task anchor and memory strip", "Grounds pupils who are easily distracted: current target, a parking lot for ideas, and a reward check.", Anchor],
  ["pr-steps", "Dyslexia and dyspraxia 3-step task strip", "Turns text-heavy instructions into three clear steps that sit on the desk.", Steps],
  ["pr-auditory", "Auditory overload tracking form", "Log sensory spikes across settings to adjust ear defender use and plan support.", Auditory],
  ["pr-contrast", "Visual tracking and contrast scan card", "Check font size, line spacing and background tint before reading materials are issued.", Contrast],
  ["pr-seizure", "Post-seizure observation sheet", "A recording sheet for staff folders covering timings, recovery stages and sign-off.", Seizure],
  ["pr-desk", "Fine-motor desk set-up audit", "A 5-point check of workspace set-up for pupils with physical or coordination needs.", Desk],
];

export default function PrintablesPage() {
  return (
    <>
      <TopBar minimal />
      <main id="main" tabIndex={-1} className="outline-none">
        <section className="no-print mx-auto max-w-[1100px] px-5 pb-10 pt-[150px] sm:px-8 lg:pt-[178px]" aria-labelledby="pr-title">
          <p className="eyebrow mb-4">Free · Print and photocopy</p>
          <h1 id="pr-title" className="font-display text-[2.6rem] font-medium leading-[1] tracking-[-0.03em] sm:text-6xl">Classroom support <span className="italic text-brand">printables</span></h1>
          <p className="mt-6 max-w-2xl text-lg text-ink-soft">High-contrast sheets and cards covering communication, emotional wellbeing, attention, literacy, sensory, visual, medical and physical needs. Print them blank and complete by hand. Use pupil reference codes, never names.</p>
          <DownloadChoices className="mt-8" file="classroom-support-printables.pdf" resource="printables" label="all printables (PDF)" testid="pr-download"
            note="One PDF with all eight sheets. Or print a single sheet with its own Print button below." />
        </section>
        <div className="print-scope mx-auto max-w-[1100px] space-y-8 px-5 pb-20 sm:px-8" data-pack="printables">
          {PRINTABLES.map(([id, title, blurb, El]) => (
            <section key={id} id={id} aria-labelledby={`${id}-title`} className="tool-section avoid-break rounded-[1.75rem] border-2 border-line bg-paper p-5 sm:p-8" data-testid={`printable-${id}`}>
              <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
                <div><h2 id={`${id}-title`} className="font-display text-2xl font-medium sm:text-3xl">{title}</h2><p className="mt-1 max-w-xl text-ink-soft">{blurb}</p></div>
                <button type="button" onClick={() => printTools(id)} className="btn btn-brand btn-sm no-print" data-testid={`${id}-print`}><Printer size={14} aria-hidden /> Print</button>
              </div>
              <div className="text-ink"><El /></div>
              <div className="print-only-url mt-6 border-t pt-2"><FullFooter /></div>
            </section>
          ))}
        </div>
      </main>
      <Footer />
    </>
  );
}
